import { calculateGaugeAngle } from './calculate-gauge-angle.js';
import type { GaugeChartThresholdIndicatorProps } from '../slots/ThresholdIndicator.js';

export interface ThresholdIntervalArcData {
  startAngle: number;
  endAngle: number;
  color: string;
}

/**
 * Converts a list of threshold indicators into colored arc segment data for
 * the threshold interval bar drawn below the gauge arc.
 *
 * Zones created (consistent with findMatchingThreshold coloring logic):
 *   [min → t[0].value]            fill-out zone  → baseColor
 *   [t[0].value → t[1].value]     zone 1          → t[0].color
 *   ...
 *   [t[N-1].value → max]          zone N           → t[N-1].color
 *
 * @param thresholds   - Threshold indicators (any order; out-of-range filtered).
 * @param min          - Chart minimum value.
 * @param max          - Chart maximum value.
 * @param midRadius    - Mid-radius of the threshold arc band; used to convert
 *                       pixel distances to angles.
 * @param baseColor    - Color for the fill-out zone (before the first threshold).
 * @param gapPx        - Circumferential gap in px between adjacent segments.
 * @param minLengthPx  - Minimum arc length in px. Gaps shrink to keep thin zones
 *                       visible; zero-width zones (duplicate values) collapse.
 */
export const buildThresholdIntervalArcs = (
  thresholds: GaugeChartThresholdIndicatorProps[],
  min: number,
  max: number,
  midRadius: number,
  baseColor: string,
  gapPx: number,
  minLengthPx: number,
): ThresholdIntervalArcData[] => {
  if (thresholds.length === 0 || midRadius <= 0) {
    return [];
  }

  const inRange = thresholds
    .filter((t) => t.value > min && t.value < max)
    .sort((a, b) => a.value - b.value);
  const boundaries = [min, ...inRange.map((t) => t.value), max];
  const zoneColors = [baseColor, ...inRange.map((t) => t.color ?? baseColor)];

  const minAngle = minLengthPx / midRadius;
  const desiredHalfGap = gapPx / midRadius / 2;
  const angles = boundaries.map((b) => calculateGaugeAngle(b, max, min));

  const arcs: ThresholdIntervalArcData[] = [];
  const lastIndex = boundaries.length - 2;

  for (let i = 0; i <= lastIndex; i++) {
    const rawSpan = angles[i + 1] - angles[i];
    let leftGap = i > 0 ? desiredHalfGap : 0;
    let rightGap = i < lastIndex ? desiredHalfGap : 0;

    // Gaps yield to segments: clamp their sum so no zone shrinks below its floor.
    const floor = Math.min(rawSpan, minAngle);
    const maxGapSum = rawSpan - floor;
    const gapSum = leftGap + rightGap;
    if (gapSum > maxGapSum) {
      const scale = gapSum > 0 ? maxGapSum / gapSum : 0;
      leftGap *= scale;
      rightGap *= scale;
    }

    const startAngle = angles[i] + leftGap;
    const endAngle = angles[i + 1] - rightGap;

    if (endAngle <= startAngle) {
      continue;
    }

    arcs.push({
      startAngle,
      endAngle,
      color: zoneColors[i],
    });
  }

  return arcs;
};
